// User types
export interface User {
  id: string
  email: string
  username: string | null
  is_onboarded: boolean
  is_internal?: boolean
  global_rule: Record<string, unknown> | null
  created_at: string
}

// Subscription types
export type PlanType = 'trial' | 'basic' | 'pro' | 'enterprise'
export type BillingCycle = 'none' | 'weekly'
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due'

export interface Subscription {
  plan_type: PlanType
  credits_total: number
  credits_used: number
  credits_available: number
  subscription_credits: number  // Subscription credits (may expire)
  purchased_credits: number     // Purchased credits (never expire)
  billing_cycle: BillingCycle
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  status: SubscriptionStatus
  is_trial: boolean
  is_expired: boolean
}

// Credit purchase types
export interface CreditPackage {
  id: string
  credits: number
  price_cents: number
  display_name: string
  max_quantity: number  // Maximum packages per purchase (1-10)
}

export type PurchaseStatus = 'pending' | 'succeeded' | 'failed' | 'refunded'

export interface CreditPurchase {
  id: string
  package_id: string
  amount_cents: number
  credits_amount: number
  status: PurchaseStatus
  created_at: string
}

export interface Plan {
  plan_type: PlanType
  name: string
  price_cents: number
  credits: number
  billing_cycle: BillingCycle
  features: string[]
  is_recommended: boolean
}

// Product types
export interface Product {
  id: string
  product_name: string
  description: string | null
  selling_points: string | null
  target_market: string | null
  target_audience: string | null
  price: string | null
  language: string
  source_url: string | null
  keywords: string[]
  competitor_brands: string[]
  screenshot_url: string | null
  screenshot_thumbnail_url?: string | null
  uploaded_count: number
  generated_count: number
  total_assets: number
  product_type: 'novel' | 'ecommerce' | 'finance'
  metadata: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export type VerticalSkillMediaType = 'image' | 'video'

export interface VerticalSkillOption {
  key: string
  label: string
  description: string
  content: string | null
  is_default: boolean
}

export interface VerticalSkillOptionsResponse {
  product_id: string
  product_type: Product['product_type']
  media_type: VerticalSkillMediaType
  default_skill_key: string | null
  options: VerticalSkillOption[]
}

// Asset types
export type AssetType = 'image' | 'video'
export type SourceType = 'screenshot' | 'uploaded' | 'generated' | 'reference' | 'feedback_reference' | 'feedback_variant'

export interface LibraryGenerationTextInput {
  key: string
  label: string
  text: string
}

export interface LibraryGenerationField {
  key: string
  label: string
  value: string
}

export interface GenerationDetails {
  workflow: 'image_freestyle' | 'video_freestyle' | 'competitor_research' | 'digital_human' | 'variation'
  text_inputs: LibraryGenerationTextInput[]
  fields: LibraryGenerationField[]
}

export interface Asset {
  id: string
  asset_type: AssetType
  source_type: SourceType
  url: string | null  // Preview/display URL (inline)
  thumbnail_url?: string | null
  download_url: string | null  // Download URL (Content-Disposition: attachment)
  file_name: string | null
  description: string | null
  score: number | null
  is_favorited: boolean
  width?: number | null
  height?: number | null
  duration_seconds?: number | null
  created_at: string
  product_id: string | null  // Associated product ID for auto-loading product info
  parent_asset_id: string | null
  idea_title?: string | null
}

export interface AssetDetail extends Asset {
  generation_details: GenerationDetails | null
}

export interface AssetDownloadResult {
  download_url: string | null
}

export interface AssetStats {
  screenshot: number
  uploaded: number
  generated: number
  reference: number
  feedback_reference: number
  feedback_variant: number
  total: number
  image: number
  video: number
}

// Generation result with reference mapping (for 1:1 competitor research)
export interface GenerationResult extends Asset {
  reference_ad_id: string | null  // Foreplay ad ID for 1:1 mapping
  first_frame_url?: string | null  // Presigned URL for video first frame image
}

// Task types
export type TaskType = 'competitor_research' | 'variation' | 'image_freestyle' | 'video_freestyle' | 'digital_human' | 'idea_research' | 'vertical_ideas' | 'foreplay_search' | 'perplexity_search' | 'short_drama_mix'
export type TaskStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
export type ToolId = 'gemini_image' | 'gemini_image_flash' | 'gpt_image' | 'sora' | 'veo' | 'seedance' | 'happyhorse' | 'foreplay' | 'perplexity' | 'deep_research' | 'document'

export interface Task {
  id: string
  task_type: TaskType
  tool_id: ToolId
  status: TaskStatus
  progress_stage?: 'script' | 'images' | 'videos'  // Current progress stage for multi-step tasks
  requested_count: number
  success_count: number
  credits_consumed: number
  error_message: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
}

// Overview page types
export interface OverviewMetrics {
  active_tasks: number
  output_7d: number
  asset_library_total: number
  product_coverage_with_assets: number
  product_coverage_total: number
}

export type OverviewAlertLevel = 'info' | 'warning' | 'danger'

export interface OverviewAlert {
  id: string
  level: OverviewAlertLevel
  title: string
  description: string
  action_label: string
  action_to: string
}

export interface OverviewTaskItem {
  id: string
  label: string
  status: TaskStatus
  progress_stage?: Task['progress_stage']
  success_count: number
  requested_count: number
  created_at: string
  credits_consumed: number
}

// Reference Ad types (from Foreplay - legacy)
export interface ReferenceAd {
  id: string
  brand: string
  headline: string | null
  body_text: string | null
  image_url: string | null
  platform: string
  likes: number
  comments: number
}

// Keyword generation types
export interface KeywordGenerationResult {
  description: string
  cate: string
  keywords: string[]
  competitor_brands: string[]
}

// Foreplay search types (v2)
export interface ForeplayAd {
  id: string
  brand_id: string | null
  brand_name: string | null
  name: string | null
  description: string | null
  ad_type: 'image' | 'video'
  url: string | null
  download_s3_url: string | null
  relevance_score: number | null
  overall_score: number | null  // Added for scoring feature
}

export interface ForeplaySearchResult {
  product_related: ForeplayAd[]
  competitor_brands: ForeplayAd[]
  search_metadata: Record<string, number>
}

// Scored ad types
export interface ScoredAd {
  ad: ForeplayAd
  product_relevance: number
  reference_quality: number
  compliance: number
  overall_score: number
  summary: string | null
}

// Score types
export interface ScoreResult {
  overall_score: number
  breakdown: Record<string, number>
  recommendations: string[]
}

// API Response types
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  offset: number
  limit: number
}

// Standard error response format from backend (middleware/error_handler.py)
export interface ApiError {
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
  }
}

// Legacy error format (for backward compatibility)
export interface LegacyApiError {
  success: false
  error_code: string
  message: string
  details?: Record<string, unknown>
}

// ========== Creative Lab Types ==========

export interface Idea {
  id: string
  product_id: string
  title: string
  description: string
  rationale: string | null
  ad_format: string | null
  source: string
  source_url: string | null
  report_source: string | null
  source_metadata: Record<string, unknown> | null
  media_urls: string[]
  tags: string[]
  is_favorited: boolean
  used_count: number
  last_used_at: string | null
  created_at: string
  updated_at: string
}

export interface IdeaCreate {
  title: string
  description: string
  rationale?: string
  ad_format?: 'image' | 'video'
  source?: string
  source_url?: string
  report_source?: string
  source_metadata?: Record<string, unknown>
  media_urls?: string[]
  tags?: string[]
}

export interface IdeaInput {
  idea_id: string
  creative_reference_url?: string | null
}

// Slot: IdeaInput for idea slots, null for freestyle slots
export type GenerationSlot = IdeaInput | null

export interface ResearchResult {
  ideas: Idea[]
  sources_used: string[]
  ideas_per_source: Record<string, number>
}

export type ResearchTab = 'all' | 'ads' | 'web' | 'deep' | 'doc'
export type ResearchTool = 'foreplay' | 'perplexity' | 'deep_research' | 'document'

export interface ResearchStartRequestPayload {
  product_id: string
  tab: ResearchTab
  tools?: ResearchTool[]
  research_direction?: string
  document_content?: string
  max_ideas?: number
}

export interface ResearchStartResponse {
  task_id: string
  status: TaskStatus
  stream_url: string
  result_url: string
}

export interface ReportDraft {
  markdown: string
  summary: string
  tools: ResearchTool[]
  sources_used: string[]
  ideas_per_source: Record<string, number>
  total_ideas: number
  idea_ids: string[]
  created_at: string
  tab: ResearchTab
  research_direction?: string | null
  model_provider?: string | null
  model_name?: string | null
}

export interface ResearchResultPayload {
  ideas: Idea[]
  sources_used: string[]
  ideas_per_source: Record<string, number>
  report_draft?: ReportDraft | null
  report_saved: boolean
}

export interface SaveResearchReportResponse {
  report_id: string
  s3_key: string
  created_at: string
}

export interface DeleteResearchReportResponse {
  success: boolean
}

export interface ResearchReportSummary {
  id: string
  task_id: string
  product_id: string
  tab: ResearchTab
  tools: ResearchTool[]
  summary?: string | null
  ideas_per_source: Record<string, number>
  total_ideas: number
  created_at: string
}

export interface ResearchReportDetail extends ResearchReportSummary {
  research_direction?: string | null
  idea_ids: string[]
  s3_key: string
  raw_s3_key?: string | null
}

export interface ReportMarkdownResponse {
  markdown: string
}

export interface ResearchStreamEvent {
  id?: number
  event: string
  data: Record<string, unknown>
  received_at: string
}

// Foreplay API types (Creative Lab - public API)
export interface ForeplayAPIAd {
  id: string
  brand_id: string | null
  brand_name: string | null
  name: string | null
  description: string | null
  display_format: string | null
  publisher_platform: string[]
  video: string | null
  thumbnail: string | null
  running_duration: Record<string, unknown> | null
  video_duration: number | null
  emotional_drivers: Record<string, unknown> | null
  market_target: string | null
  niches: string[]
  full_transcription: string | null
  raw_data: Record<string, unknown>
}

export interface ForeplayAPIBrand {
  id: string
  name: string
  logo: string | null
}

// Perplexity types
export interface PerplexitySearchItem {
  title: string
  url: string
  snippet: string
  date: string | null
  last_updated: string | null
}

export interface PerplexitySearchResult {
  id: string
  results: PerplexitySearchItem[]
}

export interface PerplexityChatResult {
  content: string
  citations: string[]
  images: Array<{
    image_url: string
    origin_url: string
    height?: number | null
    width?: number | null
  }>
  videos: Array<{
    video_url: string
    thumbnail_url?: string | null
    title?: string | null
    duration?: number | null
  }>
  model: string
  usage?: Record<string, unknown> | null
}

// Form types
export interface CreateProductForm {
  product_name: string
  description?: string
  selling_points?: string
  target_market?: string
  target_audience?: string
  price?: string
  language?: string
  source_url?: string
  screenshot_asset_id?: string | null
  keywords?: string[]
  competitor_brands?: string[]
  metadata?: Record<string, unknown>
}

export interface CreateProductFromUrlPayload {
  url: string
  product_type?: 'ecommerce' | 'finance'
}

export type VerticalIdeasProductType = 'novel' | 'ecommerce' | 'finance'

export interface NovelParsedFields {
  product_name: string
  description: string
  selling_points: string
  target_audience: string
  language: string
  novel_style: string
  keywords: string[]
  competitor_brands: string[]
}

export interface NovelParseResult {
  source_mode: 'url' | 'file' | 'text'
  raw_text: string
  parsed_fields: NovelParsedFields
  metadata: Record<string, unknown>
}

export interface VerticalIdeaMarketAnalysisRequestFields {
  analysis_content?: string
  analysis_file_name?: string
  analysis_file_mime_type?: string
}

export interface NovelScenario {
  id: string
  scene_description: string
  characters: string[]
  emotions: string[]
  visual_motifs: string[]
  chapter_reference: string
  confirmed: boolean
}

export interface GenerateNovelScenariosRequestPayload {
  scenario_count?: number
}

export interface ConfirmNovelScenariosRequestPayload {
  confirmed_scenario_ids: string[]
}

export interface NovelScenariosPayload {
  product: Product
  scenarios: NovelScenario[]
  total_scenarios: number
  confirmed_count: number
}

export type VerticalIdeasOutputMode = 'mixed' | 'image' | 'video'

export interface VerticalIdeasStartRequestPayload extends VerticalIdeaMarketAnalysisRequestFields {
  product_type: VerticalIdeasProductType
  product_id: string
  idea_count?: number
  idea_format?: VerticalIdeasOutputMode
  reference_source_filter?: string
}

export interface VerticalIdeasStartResponse {
  task_id: string
  status: TaskStatus
}

export interface VerticalIdeasResultPayload {
  task_id: string
  status: TaskStatus
  current_step: string | null
  product: Product | null
  requested_count: number
  success_count: number
  completed_batch_count: number
  total_batch_count: number
  ideas: Idea[]
  error_message: string | null
}

export interface GenerateImageForm {
  prompt: string
  model: ToolId
  count: number
  aspect_ratio: string
  image_size?: string
  image_quality?: string
  product_id?: string
  include_product_screenshot?: boolean
  reference_asset_id?: string
  reference_asset_ids?: string[]
  vertical_skill_key?: string
  slots?: GenerationSlot[]
  requirement_id?: string
}

export interface GenerateVideoForm {
  prompt: string
  model: ToolId
  duration_seconds: number
  aspect_ratio: string
  resolution?: string
  product_id?: string
  include_product_screenshot?: boolean
  reference_asset_id?: string
  reference_asset_ids?: string[]
  vertical_skill_key?: string
  slots?: GenerationSlot[]
  requirement_id?: string
}

export interface CompetitorResearchForm {
  product_id: string
  reference_ad_ids: string[]
  keywords: string[]
  target_audience?: string
  custom_prompt?: string
  model: ToolId
  count: number
  aspect_ratio?: string  // Default to "1:1" on backend
}

export interface VariationGenerateForm {
  original_asset_id: string
  variation_type: string
  custom_prompt?: string
  model: ToolId
  count: number
}

// ========== Two-Stage Variation Generation Types ==========

export type VariationDimension =
  | 'style'
  | 'scene'
  | 'composition'
  | 'mood'
  | 'color_lighting'
  | 'target_audience'

export interface VariationDirection {
  id: string
  name: string
  description: string
  dimension: VariationDimension
}

export interface AnalysisResult {
  composition: string | Record<string, unknown> | null
  color_palette: string[]
  visual_elements: string[]
  text_content: string | null
  style: string | null
  mood: string | null
}

export interface VariationAnalyzeResponse {
  analysis_id: string
  analysis: AnalysisResult
  directions: VariationDirection[]
  source_asset_url: string | null
  created_at: string
}

export interface VariationGenerateResponse {
  variation_id: string
  asset_id: string | null
  asset_url: string | null
  direction_info: VariationDirection
  prompt_used: string
  status: string
}

export interface VariationGenerateBatchResponse {
  task_id: string
  analysis_id: string
  variations: VariationGenerateResponse[]
  total_requested: number
  total_succeeded: number
  total_failed: number
  credits_consumed: number
}

export interface VariationItem {
  id: string
  direction_id: string
  direction_name: string | null
  direction_dimension: string | null
  status: 'pending' | 'processing' | 'completed' | 'failed'
  result_asset_id: string | null
  result_asset_url: string | null
  created_at: string
}

export interface VariationAnalysisDetail {
  analysis_id: string
  analysis: AnalysisResult
  directions: VariationDirection[]
  variations: VariationItem[]
  source_asset_url: string | null
  created_at: string
}

// Two-stage variation forms
export interface VariationAnalyzeForm {
  source_asset_id: string
  product_id?: string  // Bind generated assets to this product
  product_info?: Record<string, unknown>
  num_variations?: number
  user_preference?: string  // User style preferences (HIGH PRIORITY in AI analysis)
}

export interface VariationGenerateFormV2 {
  analysis_id: string
  direction_ids: string[]  // Support 1-10 directions
  aspect_ratio?: string
  tool_id?: string
  variation_degree?: number  // 10-100: subtle(10-35), moderate(36-65), bold(66-100)
  requirement_id?: string
}

// ========== Digital Human Generation Types ==========

// Digital human credit costs
export const DIGITAL_HUMAN_CREDITS = {
  COMPLETE: 10,  // Pre-roll (4s) + Digital Human (15s) + Post-roll (4s) = 23s
  MINIMAL: 5,    // Digital Human only (15s)
} as const

// Digital human aspect ratio options
export type DigitalHumanAspectRatio = '9:16' | '16:9'
export type SpokenLanguage = 'auto' | 'latin' | 'zh_hans' | 'zh_hant' | 'ja' | 'ko' | 'th'

export interface SubtitleStyle {
  font_color: string
  font_size: number
  outline_color?: string
  outline_width?: number
}

// Digital human form
export interface DigitalHumanForm {
  product_id: string                   // Required - product for script generation
  with_bumpers: boolean                // true = complete (10 credits), false = minimal (5 credits)
  aspect_ratio: DigitalHumanAspectRatio  // "9:16" (vertical) or "16:9" (horizontal)
  spoken_language?: SpokenLanguage     // Optional override for generated speech/subtitles language
  voice_id?: string                    // Optional manual HeyGen voice override
  script_suggestion?: string           // Optional - user guidance for GPT script generation
  custom_subtitle_text?: string        // Optional exact text for spoken script and optional burned subtitles
  use_product_reference_image?: boolean
  product_reference_asset_id?: string
  digital_human_reference_asset_id?: string
  with_subtitles?: boolean             // Burn subtitles onto digital human segment
  subtitle_style?: SubtitleStyle
  requirement_id?: string
}

// ---- Finance feedback types ----
export interface FeedbackBatch {
  requirement_id: string
  date: string | null
  market: string | null
  creative_type: string | null
  source_run: string | null
  variant_count: number
}

export interface FeedbackBatchStats extends FeedbackBatch {
  reviewed: number
  adopted: number
  rejected: number
  revise: number
  downloads: number
  downloaded_variants: number
}

export interface FeedbackReview {
  feedback_text: string
  decision: string | null
  score: number | null
  reviewed_by: string | null
  reviewed_at: string | null
}

export interface ReviewUpdate {
  feedback_text: string
  decision: string | null
  score: number | null
  reviewed_by: string
}

export interface FeedbackVariant {
  id: string
  external_asset_id: string | null
  variant_type: string | null
  direction: Record<string, unknown>
  verifier: Record<string, unknown>
  prompt: string | null
  generated_url: string | null
  review: FeedbackReview
  download_count: number
}

export interface DownloadResult {
  download_url: string | null
  download_count: number
}

export interface FeedbackEvidence {
  filename: string | null
  text: string
  image_url: string | null
}

export interface AttributionMetrics {
  spend: number | null
  spend_pct: number | null
  ctr: number | null
  credit_cost: number | null
  loan_cost: number | null
}

export interface CandidateAttribution {
  metrics: AttributionMetrics
  visual_summary: string | null
  attribution: string | null
  visual_levers: string[]
  color_analysis: string[]
  composition: string[]
  success_factors: string[]
}

export interface MarketAttribution {
  summary: string | null
  commonalities: string[]
  winning_recipe: string | null
  traffic_trap_warning: string | null
  reference_value: string[]
}

export interface FeedbackReferenceGroup {
  reference_asset_id: string | null
  reference_url: string | null
  reference_kind: string | null
  reference_text: string | null
  reference_memo: string | null
  core_insight: string | null
  evidence: FeedbackEvidence[]
  attribution: CandidateAttribution | null
  variants: FeedbackVariant[]
}

export interface FeedbackSection {
  variant_type: string
  groups: FeedbackReferenceGroup[]
}

export interface FeedbackBatchAssets {
  batch: FeedbackBatch
  market_attribution: MarketAttribution | null
  sections: FeedbackSection[]
}

// ---- Finance report types ----
export interface FinanceReportSectionStats {
  variant_type: string
  total: number
  reviewed: number
  adopted: number
  rejected: number
  revise: number
  downloads: number
  downloaded_variants: number
  avg_score: number | null
}

export interface FinanceReportFeedbackItem {
  asset_id: string
  variant_type: string | null
  decision: string | null
  score: number | null
  downloads: number
  feedback_text: string
  reviewed_by: string | null
}

export interface FinanceReportStats {
  total_variants: number
  reviewed: number
  adopted: number
  rejected: number
  revise: number
  adoption_rate: number | null
  downloads: number
  downloaded_variants: number
  avg_score: number | null
  reviewers: string[]
  feedback_count: number
  sections: FinanceReportSectionStats[]
  feedback_items: FinanceReportFeedbackItem[]
}

export interface FinanceReportTheme {
  theme: string
  evidence: string
}

export interface FinanceReportSectionInsight {
  variant_type: string
  insight: string
}

export interface FinanceReportAnalysis {
  overall_summary: string
  adopted_patterns: string[]
  rejected_patterns: string[]
  feedback_themes: FinanceReportTheme[]
  section_insights: FinanceReportSectionInsight[]
  recommendations: string[]
}

export interface FinanceReport {
  id: string
  report_date: string
  market: string
  creative_type: string
  requirement_id: string
  status: 'running' | 'completed' | 'failed'
  stats: Partial<FinanceReportStats>
  analysis: Partial<FinanceReportAnalysis>
  model_name: string | null
  error: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface GenerateFinanceReportsResponse {
  reports: FinanceReport[]
  skipped_markets: string[]
}

// ---- Material analytics types ----
export interface MaterialAnalyticsAuthorStats {
  author_email: string
  task_count: number
  output_image_count: number
  completed_task_count: number
  failed_task_count: number
  credits_consumed: number
  downloaded_image_count: number
}

export interface MaterialAnalyticsDailyStats {
  date: string
  task_count: number
  output_image_count: number
  downloaded_image_count: number
  credits_consumed: number
}

export interface MaterialAnalyticsSummary {
  task_count: number
  output_image_count: number
  completed_task_count: number
  failed_task_count: number
  credits_consumed: number
  downloaded_image_count: number
  author_stats: MaterialAnalyticsAuthorStats[]
  daily_stats: MaterialAnalyticsDailyStats[]
}

export interface MaterialAnalyticsProduct {
  id: string
  owner_email: string
  product_name: string
  description: string | null
  selling_points: string | null
  target_market: string | null
  target_audience: string | null
  price: string | null
  language: string
  source_url: string | null
  keywords: string[]
  competitor_brands: string[]
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface MaterialAnalyticsAssetRef {
  id: string
  role: string
  asset_type: string
  source_type: string
  url: string | null
  thumbnail_url: string | null
  file_name: string | null
  md5_hash: string | null
  phash: string | null
  width: number | null
  height: number | null
  created_at: string
  missing_reason: string | null
}

export interface MaterialAnalyticsPromptSnapshot {
  prompt_system: string | null
  prompt_visual: string | null
  prompt_user: string | null
  prompt_final: string | null
  asset_prompt: string | null
  missing_fields: string[]
}

export interface MaterialAnalyticsDownloadState {
  is_downloaded: boolean
  download_count: number | null
  last_downloaded_at: string | null
  missing_reason: string | null
}

export interface MaterialAnalyticsChainItem {
  task_id: string
  task_type: string
  task_status: string
  tool_id: string
  task_created_at: string
  task_completed_at: string | null
  requested_count: number
  success_count: number
  credits_consumed: number
  generation_params: Record<string, unknown>
  author_email: string
  author_user_id: string
  product_id: string | null
  product_name: string | null
  material_product_id: string | null
  material_product_name: string | null
  result_id: string | null
  sequence_num: number | null
  reference_ad_id: string | null
  input_signature: string | null
  group_task_total: number | null
  output: MaterialAnalyticsAssetRef
  inputs: MaterialAnalyticsAssetRef[]
  prompts: MaterialAnalyticsPromptSnapshot
  download: MaterialAnalyticsDownloadState
}

export interface MaterialAnalyticsAssetListResponse {
  items: MaterialAnalyticsChainItem[]
  total: number
  offset: number
  limit: number
}

export interface MaterialAnalyticsImageSearchMatch {
  asset_id: string
  match_type: string
  distance: number | null
  chain: MaterialAnalyticsChainItem | null
}

export interface MaterialAnalyticsImageSearchResult {
  file_name: string
  md5_hash: string | null
  phash: string | null
  error: string | null
  matches: MaterialAnalyticsImageSearchMatch[]
}

// Daily batch review types (internal)
export type ReviewRating = 'use' | 'drop'

export interface ReviewBatchSummary {
  id: string
  name: string
  batch_date: string | null
  status: 'uploading' | 'ready'
  stats: {
    shoot_count?: number
    file_count?: number
    video_count?: number
    pass?: number
    fail?: number
    countries?: Record<string, number>
  }
  reviewer_count: number
  created_at: string
  updated_at: string
}

export interface ReviewAsset {
  id: string
  asset_type: 'image' | 'video' | 'document'
  role: 'canonical_variant' | 'raw_variant' | 'face' | 'panel' | 'composite' | 'doc' | ''
  variant: number | null
  run_id: string | null
  retry: number
  pass_fail: string | null
  relpath: string
  filename: string
  mime_type: string | null
  file_size: number | null
  preview_url: string | null
  download_url: string | null
  thumbnail_url: string | null
}

export interface VariantFeedback {
  reviewer_email: string
  variant: number
  rating: ReviewRating | null
  post_processing_pct: number
  changes: string[]
  note: string | null
  updated_at?: string | null
}

export interface ReviewShootDetail {
  id: string
  country: string
  slug: string
  style: string | null
  human_mode: string | null
  shot_mode: string | null
  n_panels: number | null
  idea: Record<string, unknown>
  prompt: string | null
  subtitles: Record<string, string>
  index_md: string | null
  variant_results: Record<string, { result?: string; fails?: string[] }>
  assets: ReviewAsset[]
  reviews: VariantFeedback[]
}

export interface AdoptionRow {
  shoot_id: string
  country: string
  slug: string
  variant: number
  reviewer_email: string
  post_processing_pct: number
  changes: string[]
  note: string | null
}

export interface ReviewBatchDetail {
  batch: ReviewBatchSummary
  adoption: AdoptionRow[]
  countries: { country: string; shoots: ReviewShootDetail[] }[]
}

export interface VariantReviewInput {
  rating: ReviewRating | null
  post_processing_pct: number
  changes: string[]
  note: string | null
}

export interface BatchReviewsPayload {
  batch_id: string
  reviews: (VariantFeedback & { shoot_id: string; country: string | null; slug: string | null })[]
}
